import axios, { AxiosInstance } from "axios";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from "react-toastify";
import StorageService from "../services/StorageService";
import { encryptWithAes, generateAesIV, generateAesKey } from "../helpers/AesHelper";
import IEncryptedApiRequest from "../models/IEncryptedApiRequest";
import { encryptWithPublicKey } from "../helpers/RsaHelper";
import { showSessionExpiredToast } from "../helpers/ToastifyHelper";
const activeToasts = new Map<string, string>();

export abstract class BaseAPI {
  protected axiosInstance: AxiosInstance | any = null;

  constructor(baseUrl: string) {
    this.axiosInstance = axios.create({ baseURL: baseUrl, withCredentials: true });
    this.addResponseInterceptor();
  }

  private defaultErrorMessage = "Beklenmedik bir hata oluştu lütfen daha sonra tekrar deneyiniz.";

  // CSRF token'ı çerezden almak için yardımcı fonksiyon
  private getCookie(name: string): string | null {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  protected get(endpoint?: string, params?: any, encrypted: boolean = false): Promise<any> {
    if (encrypted) {
      var aesKey = generateAesKey();
      var aesIv = generateAesIV();
      let encryptedData = encryptWithAes(aesKey, aesIv, JSON.stringify(params));
      let encryptedAes = encryptWithPublicKey(aesKey + aesIv);
      if (encryptedAes) {
        let enryptedRequest: IEncryptedApiRequest = {
          encryptedData: encryptedData,
          aesKey: encryptedAes
        }
        return this.axiosInstance.get(endpoint, enryptedRequest).then((response: any) => {
          return response.data;
        });
      } else {
        return Promise.reject();
      }
    }
    else {
      return this.axiosInstance.get(endpoint, params).then((response: any) => {
        return response.data;
      });
    }
  }

  protected getFile(endpoint?: string, params?: any): Promise<any> {
    params.responseType = 'blob';
    params.observe = 'response';
    return this.axiosInstance.get(endpoint, params).then((response: any) => {
      return response;
    });
  }

  protected post(endpoint: string, params?: any, encrypted: boolean = false): Promise<any> {
    // CSRF token'ı al
    const token = this.getCookie('XSRF-TOKEN');

    const headers: any = {};
    if (token) {
      headers['X-XSRF-TOKEN'] = token;
    }

    if (encrypted) {
      var aesKey = generateAesKey();
      var aesIv = generateAesIV();
      let encryptedData = encryptWithAes(aesKey, aesIv, JSON.stringify(params));
      let encryptedAes = encryptWithPublicKey(aesKey + aesIv);
      if (encryptedAes) {
        let enryptedRequest: IEncryptedApiRequest = {
          encryptedData: encryptedData,
          aesKey: encryptedAes
        }
        return this.axiosInstance.post(endpoint, enryptedRequest, { headers }).then((response: any) => {
          return response.data;
        });
      } else {
        toast.error(this.defaultErrorMessage);
        return Promise.reject();
      }
    } else {
      return this.axiosInstance.post(endpoint, params, { headers }).then((response: any) => {
        return response.data;
      });
    }
  }

  // Aktif toast mesajlarını takip eden yapı

  private addResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        const message = error.response?.data?.message || this.defaultErrorMessage;

        const requestUrl: string = error.config?.url?.toLowerCase() || "";

        //  Eğer URL içinde "login" geçiyorsa, toast gösterme
        if (requestUrl.includes("/login")) {
          return Promise.reject(error);
        }

        if (!activeToasts.has(message)) {
          const requestUrl = (error?.config?.baseURL || "") + (error?.config?.url || "");

          // Sadece SmsDogrulamaSave URL'leri için süreyi uzat
          if (requestUrl.includes("SmsDogrulamaSave")) {
            const toastId = toast.error(message, {
              autoClose: 20000, // SMS için uzun süre
              onClose: () => {
                activeToasts.delete(message);
              }
            });
            activeToasts.set(message, toastId.toString());
          }
        }

        if (error.response) {
          if (error.response.status === 401 || error.response.status === 403) {
            StorageService.clearAll();
            showSessionExpiredToast(() => {
              window.location.href = '/login';
            });
          } else {
            if (!activeToasts.has(message)) {
              const toastId = toast.error(message, {
                onClose: () => {
                  activeToasts.delete(message);
                }
              });
              activeToasts.set(message, toastId.toString());
            }
          }
        } else {
          if (!activeToasts.has(this.defaultErrorMessage)) {
            const toastId = toast.error(this.defaultErrorMessage, {
              onClose: () => {
                activeToasts.delete(this.defaultErrorMessage);
              }
            });
            activeToasts.set(this.defaultErrorMessage, toastId.toString());
          }
        }

        return Promise.reject(error);
      }
    );
  }



}
